import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Select from '../Select';
import Typography from '../Typography';
import Button from '../Button';
import HotkeysPreferences from '../HotkeysPreferences';
import { ButtonEnums } from '../Button';

// Access lite mode service from window (exposed by cornerstone extension)
const getLiteModeService = () => (window as any).liteModeService;

const UserPreferences = ({
  availableLanguages,
  defaultLanguage,
  currentLanguage,
  disabled = false,
  hotkeyDefinitions,
  hotkeyDefaults,
  onCancel = () => {},
  onSubmit = () => {},
  onReset = () => {},
  hotkeysModule,
  additionalPreferenceTabs = [],
}) => {
  const { t } = useTranslation('UserPreferencesModal');
  const [state, setState] = useState({
    isDisabled: disabled,
    hotkeyErrors: {},
    hotkeyDefinitions,
    language: currentLanguage,
  });

  // Lite Mode state
  const [liteModeEnabled, setLiteModeEnabled] = useState(false);
  const liteModeService = getLiteModeService();
  const [activeTab, setActiveTab] = useState('general');
  const tabLifecycles = useRef(new Map());
  const visiblePreferenceTabs = useMemo(
    () => additionalPreferenceTabs.filter(tab => tab.isVisible?.() !== false),
    [additionalPreferenceTabs]
  );

  const registerTabLifecycle = useCallback((tabId, lifecycle) => {
    if (lifecycle) {
      tabLifecycles.current.set(tabId, lifecycle);
    } else {
      tabLifecycles.current.delete(tabId);
    }
  }, []);

  useEffect(() => {
    if (liteModeService?.isEnabled) {
      setLiteModeEnabled(liteModeService.isEnabled());
    }
  }, []);

  const handleLiteModeToggle = () => {
    if (!liteModeService) {
      return;
    }

    if (liteModeEnabled) {
      liteModeService.disable();
    } else {
      liteModeService.enable('user', true);
    }
  };

  const onSubmitHandler = async () => {
    const results = await Promise.all(
      Array.from(tabLifecycles.current.values()).map(lifecycle => lifecycle.save?.())
    );
    if (results.includes(false)) {
      return;
    }
    onSubmit(state);
  };

  const onResetHandler = () => {
    tabLifecycles.current.forEach(lifecycle => lifecycle.reset?.());
    setState(state => ({
      ...state,
      language: defaultLanguage,
      hotkeyDefinitions: hotkeyDefaults,
      hotkeyErrors: {},
      isDisabled: disabled,
    }));
    onReset();
  };

  const onCancelHandler = () => {
    tabLifecycles.current.forEach(lifecycle => lifecycle.cancel?.());
    setState({ hotkeyDefinitions });
    onCancel();
  };

  const onLanguageChangeHandler = value => {
    setState(state => ({ ...state, language: value }));
  };

  const onHotkeysChangeHandler = (id, definition, errors) => {
    setState(state => ({
      ...state,
      isDisabled: Object.values(errors).every(e => e !== undefined),
      hotkeyErrors: errors,
      hotkeyDefinitions: {
        ...state.hotkeyDefinitions,
        [id]: definition,
      },
    }));
  };

  const Section = ({ title, children }) => (
    <>
      <div className="mb-2 border-b-2 border-black">
        <Typography
          variant="inherit"
          color="primaryLight"
          className="flex pb-2 text-[16px] font-semibold !leading-[1.2]"
        >
          {title}
        </Typography>
      </div>
      <div className="mt-4 mb-8">{children}</div>
    </>
  );

  return (
    <>
      {visiblePreferenceTabs.length > 0 && (
        <div
          className="mb-6 flex border-b border-[#3a4356]"
          role="tablist"
        >
          {[{ id: 'general', label: t('General') }, ...visiblePreferenceTabs].map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`border-b-2 px-4 py-2 text-sm font-medium ${
                activeTab === tab.id
                  ? 'border-primary-light text-primary-light'
                  : 'border-transparent text-gray-300 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <div className={activeTab === 'general' ? '' : 'hidden'}>
        <Section title={t('General')}>
          <div className="flex w-72 flex-row items-center justify-center">
            <Typography
              variant="subtitle"
              className="mr-5 h-full text-right"
            >
              {t('Language')}
            </Typography>
            <Select
              className="user-preferences-select"
              isClearable={false}
              onChange={onLanguageChangeHandler}
              options={availableLanguages}
              value={state.language}
            />
          </div>
          {liteModeService && (
            <div className="mt-4 flex w-full flex-row items-center justify-center">
              <Typography
                variant="subtitle"
                className="mr-5 h-full text-right"
              >
                Modo Lite
              </Typography>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleLiteModeToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    liteModeEnabled ? 'bg-blue-600' : 'bg-gray-600'
                  }`}
                  role="switch"
                  aria-checked={liteModeEnabled}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      liteModeEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <Typography
                  variant="body"
                  className="text-gray-400"
                >
                  {liteModeEnabled ? 'Renderizado por CPU' : 'Renderizado por GPU'}
                </Typography>
              </div>
            </div>
          )}
        </Section>
        <Section title={t('Hotkeys')}>
          <HotkeysPreferences
            disabled={disabled}
            hotkeyDefinitions={state.hotkeyDefinitions}
            onChange={onHotkeysChangeHandler}
            errors={state.hotkeyErrors}
            hotkeysModule={hotkeysModule}
          />
        </Section>
      </div>
      {visiblePreferenceTabs.map(tab => (
        <div
          key={tab.id}
          role="tabpanel"
          className={activeTab === tab.id ? '' : 'hidden'}
        >
          {tab.render({
            registerLifecycle: lifecycle => registerTabLifecycle(tab.id, lifecycle),
          })}
        </div>
      ))}
      <div
        className={`mt-4 flex flex-row border-t border-[#3a4356] pt-4 ${
          activeTab === 'control-center' ? 'justify-end' : 'justify-between'
        }`}
      >
        {activeTab !== 'control-center' && (
          <Button
            type={ButtonEnums.type.secondary}
            onClick={onResetHandler}
            disabled={disabled}
            className="!rounded-lg !border !border-[#6b7280] !bg-[#15181f] !font-medium !text-white hover:!bg-[#003865]"
          >
            <span className="sm:hidden">Restaurar</span>
            <span className="hidden sm:inline">{t('Reset to defaults')}</span>
          </Button>
        )}
        <div className="flex flex-row">
          <Button
            type={ButtonEnums.type.secondary}
            onClick={onCancelHandler}
            className="!rounded-lg !border !border-[#6b7280] !bg-[#15181f] !font-medium !text-white hover:!bg-[#003865]"
          >
            {t('Cancel')}
          </Button>
          <Button
            disabled={state.isDisabled}
            className={`ml-2 ${
              state.isDisabled
                ? ''
                : '!rounded-lg !bg-[#8fd0fa] !font-medium !text-[#232b3b] hover:!bg-[#b3e0fc]'
            }`}
            onClick={onSubmitHandler}
          >
            {t('Save')}
          </Button>
        </div>
      </div>
    </>
  );
};

const noop = () => {};

UserPreferences.propTypes = {
  disabled: PropTypes.bool,
  hotkeyDefaults: PropTypes.object.isRequired,
  hotkeyDefinitions: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  hotkeysModule: PropTypes.shape({
    initialize: PropTypes.func.isRequired,
    pause: PropTypes.func.isRequired,
    unpause: PropTypes.func.isRequired,
    startRecording: PropTypes.func.isRequired,
    record: PropTypes.func.isRequired,
  }).isRequired,
  additionalPreferenceTabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      render: PropTypes.func.isRequired,
      isVisible: PropTypes.func,
    })
  ),
};

export default UserPreferences;
